import { Div } from '@codesuma/baseline'
import { IDayCell } from '../../../../services/calendar'
import { CalendarSystem } from '../../../../services/settings'
import { toFaDigits } from '../../../../helpers/digits'
import { J_MONTHS, G_MONTHS_EN } from '../../../../configs/names'
import { IDayColorChoice } from '../../../../configs/day-colors'
import { paintDayColor } from '../../../../helpers/day-color'
import style from './index.module.css'

interface Opts {
    system: CalendarSystem
    showSecondary: boolean
    showHijri: boolean
    isHoliday: boolean // weekend or an occasion holiday — computed by the caller
    isHolidayOther: boolean // a day off in a country the user added, but not here
    hasCustom: boolean // the user has an occasion of their own on this day
    hasEvent: boolean // ...or an event of their own — the two share one mark
    color?: IDayColorChoice // the colour the user gave the day, if any
}

// One day of the grid, expressed in up to three calendars: the primary day
// number centered, the other solar calendar in the bottom-right corner and the
// hijri day in the top-left. The corners are positioned out of flow, so
// toggling either one never shifts the day number. Days of the months either
// side are drawn the same way: the month slide leaves them out of its grid and
// shows them in the day view's line. A day the user put something of their own
// on wears a dot in the top-right corner: an occasion they made or an event
// they made, one mark for both — tapping the cell is what says which. The
// phone's own calendar is deliberately left out of it: against a subscribed
// holidays or birthdays calendar it marked nearly every day of the month, and
// the dot stopped meaning anything. A day the user gave a colour of its own
// sits on a disc of it (see services/day-colors). Presentational except the
// tap: a cell emits 'open' to navigate to the day page. The caller passes
// `isHoliday`, `hasCustom`, `hasEvent` and `color`, all of which need more
// than the cell itself.
export const DayCell = (c: IDayCell, { system, showSecondary, showHijri, isHoliday, isHolidayOther, hasCustom, hasEvent, color }: Opts) => {
    const cell = Div()
    cell.addClass(style.day)

    // Both can be true. The stylesheet decides which colour wins — see there.
    if (isHolidayOther) cell.addClass(style.holidayOther)
    if (isHoliday) cell.addClass(style.holiday)
    if (c.isToday) cell.addClass(style.today)
    // Which small dates it holds: the disc behind the day centres on them.
    if (showSecondary) cell.addClass(style.withSecondary)
    if (showHijri) cell.addClass(style.withHijri)

    const main = Div(system === 'gregorian' ? String(c.gd) : toFaDigits(c.jd))
    main.addClass(style.main)
    cell.append(main)

    if (showSecondary) {
        const secondary = Div(system === 'gregorian' ? toFaDigits(c.jd) : String(c.gd))
        secondary.addClass(style.secondary)
        cell.append(secondary)
    }

    if (showHijri) {
        const hijri = Div(toFaDigits(c.hd))
        hijri.addClass(style.hijri)
        cell.append(hijri)
    }

    if (hasCustom || hasEvent) {
        const mark = Div()
        mark.addClass(style.mark)
        cell.append(mark)
    }

    // The day's own colour, as a disc under everything else in the cell — see
    // .colorDisc, and helpers/day-color for the shade it takes.
    if (color) {
        cell.addClass(style.colored)
        const disc = Div()
        disc.addClass(style.colorDisc)
        paintDayColor(disc.el, color)
        cell.append(disc)
    }

    // Tappable: opens the day page. A real keyboard/AT target, not just a click
    // zone — the grid is the app's primary navigation.
    cell.addClass(style.clickable)
    cell.attr('role', 'button')
    cell.attr('tabindex', '0')
    // The dot is decoration, and the cell's own label hides the children from
    // assistive tech — so the label says what it stands for instead.
    const date = system === 'gregorian'
        ? `${c.gd} ${G_MONTHS_EN[c.gm - 1]}`
        : `${toFaDigits(c.jd)} ${J_MONTHS[c.jm - 1]}`
    const notes: string[] = []
    if (hasCustom || hasEvent) notes.push('رویداد شما')
    cell.attr('aria-label', [date, ...notes].join('، '))

    // Tactile press feedback (same dip as Papa List's circles). The month slide
    // presses it, since it decides which day a press is on: between the days,
    // the nearest one.
    const press = (down: boolean) => {
        cell.style({
            transition: down ? 'transform 0.12s ease' : 'transform 0.2s ease',
            transform: `scale(${down ? 0.9 : 1})`,
        })
    }

    cell.el.onclick = () => cell.emit('open')
    cell.el.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            cell.el.click()
        }
    })

    return Object.assign(cell, { press })
}
